import type { BadgeTone } from '@pv-frontend/ui-components-web';

/** The crawl-run lifecycle, mirrored from the `CrawlRunStatus` GraphQL enum. */
export type CrawlRunStatus =
    | 'QUEUED'
    | 'RUNNING'
    | 'COMPLETED'
    | 'COMPLETED_WITH_ERRORS'
    | 'FAILED';

export type CrawlTrigger = 'MANUAL' | 'NIGHTLY';
export type CrawlRunScope = 'ALL_SITES' | 'SINGLE_SITE';
export type CrawlDisposition =
    | 'CRAWLED'
    | 'SKIPPED_NOT_CRAWLABLE'
    | 'ROBOTS_DISALLOWED'
    | 'ACCESS_BLOCKED'
    | 'FAILED';

/** How much of a site's book a crawl enumerated, mirrored from the `CrawlCoverage` GraphQL enum. */
export type CrawlCoverage = 'FULL' | 'UNCHANGED' | 'PARTIAL';

export interface BadgeLook {
    label: string;
    tone: BadgeTone;
}

const STATUS: Record<CrawlRunStatus, BadgeLook> = {
    QUEUED: { label: 'Queued', tone: 'neutral' },
    RUNNING: { label: 'Running', tone: 'primary' },
    COMPLETED: { label: 'Completed', tone: 'success' },
    COMPLETED_WITH_ERRORS: { label: 'Completed with errors', tone: 'warning' },
    FAILED: { label: 'Failed', tone: 'critical' },
};

const DISPOSITION: Record<CrawlDisposition, BadgeLook> = {
    CRAWLED: { label: 'Crawled', tone: 'success' },
    SKIPPED_NOT_CRAWLABLE: { label: 'Skipped', tone: 'neutral' },
    ROBOTS_DISALLOWED: { label: 'robots.txt', tone: 'warning' },
    ACCESS_BLOCKED: { label: 'Access blocked', tone: 'warning' },
    FAILED: { label: 'Failed', tone: 'critical' },
};

const TRIGGER: Record<CrawlTrigger, string> = { MANUAL: 'Manual', NIGHTLY: 'Nightly' };
const SCOPE: Record<CrawlRunScope, string> = { ALL_SITES: 'All sites', SINGLE_SITE: 'Single site' };

/** Badge look for a run's status — falls back to a neutral badge for an unknown value. */
export function statusBadge(status: string): BadgeLook {
    return STATUS[status as CrawlRunStatus] ?? { label: status, tone: 'neutral' };
}

/** Badge look for a site's disposition within a run. */
export function dispositionBadge(disposition: string): BadgeLook {
    return DISPOSITION[disposition as CrawlDisposition] ?? { label: disposition, tone: 'neutral' };
}

/**
 * The badge that breaks the tie a disposition can't (pv-mono#266): a site that walked page 1 and gave up
 * reports the same green "Crawled" as one that walked all 72. Only the case worth an operator's attention
 * gets a badge — a crawl that reported success while seeing part of the book. A full walk stays quiet
 * (it's the norm), and a failed or blocked site already announces itself through its disposition, so
 * badging it "Partial" too would just be noise.
 */
export function coverageBadge(coverage: string, disposition: string): BadgeLook | null {
    if (disposition !== 'CRAWLED' || coverage !== 'PARTIAL') return null;
    return { label: 'Partial', tone: 'warning' };
}

/**
 * The run-level warning: some of this run's sites reported success on part of a book, so its listing
 * counts understate the market. Null when every site was fully enumerated — nothing to say.
 */
export function partialCoverageWarning(sitesPartiallyCrawled: number): string | null {
    if (sitesPartiallyCrawled < 1) return null;
    return sitesPartiallyCrawled === 1
        ? '1 site partially crawled'
        : `${sitesPartiallyCrawled} sites partially crawled`;
}

export function triggerLabel(trigger: string): string {
    return TRIGGER[trigger as CrawlTrigger] ?? trigger;
}

export function scopeLabel(scope: string): string {
    return SCOPE[scope as CrawlRunScope] ?? scope;
}

/**
 * The two states in which a run is still in flight. The list and drill-in poll
 * while any run reports one of these, and stop once every run is terminal.
 */
export function isActiveStatus(status: string): boolean {
    return status === 'QUEUED' || status === 'RUNNING';
}

/** Compact rollup line — the added/updated/missed totals shared by the list and drill-in header. */
export function rollupSummary(run: {
    listingsAdded: number;
    listingsUpdated: number;
    listingsMissed: number;
}): string {
    return `+${run.listingsAdded} · ~${run.listingsUpdated} · −${run.listingsMissed}`;
}

/** Locale date-time for a UTC ISO timestamp, or an em dash when absent. */
export function formatTimestamp(iso: string | null | undefined): string {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
